import * as moment from "moment";
import { AccionAgregada, IntervalView, MarksSelected } from "../../../interface/interfaces";
import { validarInicioEnOtraJornada, validarSolapeJornadaVecina } from "../../new-request-extemp/validators/validators-extemp";

export function computeNewPosition(
  sHour: string,
  date: moment.Moment,
  isNextDay: boolean,
  markSelected: MarksSelected,
  intervals: IntervalView[],
  acciones: AccionAgregada[]
): { intervalNum: number; type: string } | null {
  if (!sHour) return null;
  const isEdit = !!markSelected.sOriginalHour;
  const baseDateStr = date.format('YYYY-MM-DD');
  const newDateStr = isNextDay ? date.clone().add(1, 'days').format('YYYY-MM-DD') : baseDateStr;
  const newTime = moment(`${newDateStr} ${sHour}`, 'YYYY-MM-DD HH:mm');

  const times: moment.Moment[] = [];

  for (const inv of intervals) {
    const processSlot = (slotType: number, markTime: Date | null | undefined) => {
      if (!markTime) return;
      const isCurrentSlot = inv.nInterval === markSelected.nInterval && slotType === markSelected.nType_Mark;
      const editAcc = acciones.find(a => a.nInterval === inv.nInterval && a.nType_Mark === slotType && a.actionType === 'edit');
      const delAcc = acciones.find(a => a.nInterval === inv.nInterval && a.nType_Mark === slotType && a.actionType === 'delete');
      if (delAcc) return;
      if (isCurrentSlot && isEdit) return;
      times.push(editAcc ? moment(`${baseDateStr} ${editAcc.sHour}`, 'YYYY-MM-DD HH:mm') : moment(markTime));
    };
    processSlot(1, inv.inicio?.dTiempo_Marca);
    processSlot(2, inv.fin?.dTiempo_Marca);
  }

  for (const acc of acciones) {
    if (acc.actionType !== 'add') continue;
    const isCurrentSlot = acc.nInterval === markSelected.nInterval && acc.nType_Mark === markSelected.nType_Mark;
    if (isCurrentSlot && !isEdit) continue;
    const accTime = acc.dDate
      ? moment(acc.dDate)
      : moment(`${baseDateStr} ${acc.sHour}`, 'YYYY-MM-DD HH:mm');
    times.push(accTime);
  }

  times.push(newTime);
  times.sort((a, b) => a.diff(b));

  const pos = times.findIndex(t => t.isSame(newTime, 'minute'));
  if (pos === -1) return null;

  return {
    intervalNum: Math.floor(pos / 2) + 1,
    type: pos % 2 === 0 ? 'Inicio' : 'Fin',
  };
}

const formatMsg = (mom: moment.Moment, fechaBaseStr: string): string => {
  return mom.format('YYYY-MM-DD') !== fechaBaseStr
    ? `${mom.format('HH:mm')} (Día sig.)`
    : mom.format('HH:mm');
};

const buildOccupiedTimes = (
  intervals: IntervalView[],
  acciones: AccionAgregada[],
  nInterval: number,
  nType_Mark: number
): moment.Moment[] => {
  const times: moment.Moment[] = [];

  for (const inv of intervals) {
    const actionForSlot = (type: number) =>
      acciones.find(a => a.nInterval === inv.nInterval && a.nType_Mark === type);

    const addMarkTime = (markTime: Date | undefined, slotType: number) => {
      if (markTime && !(inv.nInterval === nInterval && slotType === nType_Mark)) {
        const action = actionForSlot(slotType);
        if (action?.actionType !== 'delete') {
          times.push(moment(action?.actionType === 'edit' ? action.dDate : markTime));
        }
      }
    };

    addMarkTime(inv.inicio?.dTiempo_Marca, 1);
    addMarkTime(inv.fin?.dTiempo_Marca, 2);
  }

  // Pending ADD actions (excluding current slot)
  for (const acc of acciones) {
    if (acc.nInterval === nInterval && acc.nType_Mark === nType_Mark) continue;
    if (acc.actionType === 'add') {
      times.push(moment(acc.dDate));
    }
  }

  return times;
};

const isBeforeOriginalHour = (
  horaIngresada: moment.Moment,
  markSelected: MarksSelected,
  actionDate: Date
): boolean => {
  if (!markSelected.sOriginalHour) return false;
  const originalDay = moment(actionDate);
  if (markSelected.isNextDay) originalDay.add(1, 'days');
  const originalMoment = moment(`${originalDay.format('YYYY-MM-DD')} ${markSelected.sOriginalHour}`, 'YYYY-MM-DD HH:mm');
  return horaIngresada.isBefore(originalMoment, 'minute');
};

const buildSimulatedActions = (
  hour: string,
  markSelected: MarksSelected,
  actionDate: Date,
  acciones: AccionAgregada[],
  isNextDay: boolean
): AccionAgregada[] => {
  const { nInterval, nType_Mark, sOriginalHour } = markSelected;
  const simulated = acciones.filter(a =>
    !(a.nInterval === nInterval && a.nType_Mark === nType_Mark && (a.actionType === 'add' || a.actionType === 'edit'))
  );
  const simulatedDate = moment(actionDate);
  if (isNextDay) simulatedDate.add(1, 'days');
  simulated.push({
    nInterval,
    nType_Mark,
    sHour: hour,
    sReason: '',
    dDate: moment(`${simulatedDate.format('YYYY-MM-DD')} ${hour}`, 'YYYY-MM-DD HH:mm').toISOString(),
    actionType: sOriginalHour ? 'edit' : 'add',
    sOriginalHour
  });
  return simulated;
};

export function validarHora(
  hour: string,
  markSelected: MarksSelected | null,
  actionDate: Date,
  intervals: IntervalView[],
  acciones: AccionAgregada[],
  isNextDay: boolean = false,
  intervalosVecinos: IntervalView[] = []
): string {
  if (!markSelected || !hour) return '';

  const { nInterval, nType_Mark, sOriginalHour } = markSelected;
  const fechaBaseMoment = moment(actionDate);
  const fechaBaseStr = fechaBaseMoment.format('YYYY-MM-DD');

  const workingDate = moment(actionDate);
  if (isNextDay) workingDate.add(1, 'days');
  const horaIngresada = moment(`${workingDate.format('YYYY-MM-DD')} ${hour}`, 'YYYY-MM-DD HH:mm');

  if (horaIngresada.isAfter(moment(), 'minute')) {
    return `No puedes registrar una marca mayor a la hora actual`;
  }

  if (isBeforeOriginalHour(horaIngresada, markSelected, actionDate)) {
    return `No puedes editar marcaciones para horas pasadas a la original`;
  }

  const occupied = buildOccupiedTimes(intervals, acciones, nInterval, nType_Mark);
  const simulated = buildSimulatedActions(hour, markSelected, actionDate, acciones, isNextDay);
  const otraJornada = validarInicioEnOtraJornada(actionDate, intervals, simulated);
  if (otraJornada) return otraJornada;

  const horaCompleta = moment(hour, 'HH:mm', true).isValid();
  if (horaCompleta) {
    const solape = validarSolapeJornadaVecina(horaIngresada, intervalosVecinos);
    if (solape) return solape;
  }

  for (const t of occupied) {
    if (horaIngresada.isSame(t, 'minute')) {
      return `Ya existe una marca a las ${formatMsg(t, fechaBaseStr)}.`;
    }
  }

  // For EDIT: block no-op (same hour as original)
  if (sOriginalHour && sOriginalHour === hour && !!markSelected.isNextDay === isNextDay) {
    return `Ya existe una marca a las ${formatMsg(horaIngresada, fechaBaseStr)}.`;
  }

  return '';
}
